/**
 * The panels a workspace can be built from.
 *
 * A registry rather than a union in a component, for the same reason the
 * instrument universe is data rather than code: a panel is a thing a trader
 * arranges, so the set of them has to be enumerable, describable and
 * persistable. A layout stored last month names panels by id, and this is what
 * decides whether that id still means anything.
 */

/**
 * Every panel, in the order a picker offers them.
 *
 * Ordered by how often a trader reaches for one during a session rather than
 * alphabetically — the top of this list is what a new workspace suggests.
 */
export const PANEL_KINDS = [
  'chart', 'analysis', 'rules', 'positions', 'book', 'tape', 'footprint',
  'profile', 'studies', 'news', 'journal', 'precedent', 'portfolio', 'notes',
] as const;

export type PanelKind = (typeof PANEL_KINDS)[number];

export interface PanelSpec {
  readonly kind: PanelKind;
  readonly label: string;
  /** One line for the picker. Says what it is, not why it is good. */
  readonly summary: string;
  /**
   * Smallest width in pixels at which this panel is still readable.
   *
   * Not a style value — it decides whether a column can hold it at all, and
   * whether a detached window is worth offering. A depth ladder below about
   * 280px stops being a ladder and becomes a list of clipped numbers.
   */
  readonly minWidth: number;
  /**
   * True when the panel is about the CHARTED SYMBOL rather than the account.
   *
   * Decides what a detached window has to be told: a symbol-scoped panel needs
   * to know which symbol, and stops making sense if the main window changes
   * symbol underneath it without saying so.
   */
  readonly symbolScoped: boolean;
  /**
   * True when the panel streams market data.
   *
   * Every one of these opens a relay subscription, and a workspace with four of
   * them open in four windows is four subscriptions on one entitlement. That
   * is allowed — the relay fans one upstream session out — but it is worth
   * being able to count them, which is what this is for.
   */
  readonly streams: boolean;
  /**
   * True when a workspace may hold more than one of this panel.
   *
   * Charts, and only charts, to begin with. Four charts is a trading screen;
   * four rule trackers is four copies of one account's limits, and four
   * journals is the same table four times. The flag is data rather than a
   * hard-coded `kind === 'chart'` so a second one can be allowed by editing a
   * row — the same reason the instrument universe is data (CLAUDE.md).
   *
   * A panel that is NOT multiple keeps `id === kind`, which is what lets a
   * layout stored before instances existed load unchanged.
   */
  readonly multiple: boolean;
}

const SPECS: Record<PanelKind, PanelSpec> = {
  chart:     { kind: 'chart',     label: 'Chart',            summary: 'Candles, annotations and drawings.',            minWidth: 420, symbolScoped: true,  streams: true, multiple: true },
  analysis:  { kind: 'analysis',  label: 'AEGIS analysis',   summary: "The read, its evidence and what invalidates it.", minWidth: 280, symbolScoped: true,  streams: false, multiple: false },
  rules:     { kind: 'rules',     label: 'Rule tracker',     summary: 'Every limit, the buffer left, and the projection.', minWidth: 300, symbolScoped: false, streams: false, multiple: false },
  positions: { kind: 'positions', label: 'Positions',        summary: 'What is open, and at what.',                    minWidth: 260, symbolScoped: false, streams: true, multiple: false },
  book:      { kind: 'book',      label: 'Order book',       summary: 'Depth of market on the charted symbol.',        minWidth: 280, symbolScoped: true,  streams: true, multiple: true },
  tape:      { kind: 'tape',      label: 'Time and sales',   summary: 'Prints as they arrive.',                        minWidth: 240, symbolScoped: true,  streams: true, multiple: true },
  // 360px: a footprint column needs room for two numbers side by side at every
  // price, and below that the two collapse into one unreadable smear.
  footprint: { kind: 'footprint', label: 'Footprint',        summary: 'Volume at each price, split by aggressor.',     minWidth: 360, symbolScoped: true,  streams: true, multiple: true },
  // 300px: a profile row is a horizontal bar, a price and up to a dozen TPO
  // letters side by side. Narrower and the bars stop being comparable, which
  // is the only thing the picture is for.
  profile:   { kind: 'profile',   label: 'Market profile',   summary: 'Where price spent its time, and how much traded there.', minWidth: 300, symbolScoped: true,  streams: false, multiple: true },
  // 300px: an input row is a label and a control side by side, and the source
  // of somebody else's script has to be readable without wrapping every line.
  // NOT symbol-scoped: a script is installed once and runs on every chart, so a
  // panel per symbol would offer the same list several times.
  studies:   { kind: 'studies',   label: 'Studies',          summary: 'Your custom indicators, and ones people shared with you.', minWidth: 300, symbolScoped: false, streams: false, multiple: false },
  news:      { kind: 'news',      label: 'News',             summary: 'Releases and headlines, ranked against your book.', minWidth: 300, symbolScoped: false, streams: false, multiple: false },
  journal:   { kind: 'journal',   label: 'Journal',          summary: 'Closed trades, with excursions.',               minWidth: 420, symbolScoped: false, streams: false, multiple: false },
  precedent: { kind: 'precedent', label: 'Precedent',        summary: 'How you have done in the situation on screen.', minWidth: 280, symbolScoped: true,  streams: false, multiple: false },
  portfolio: { kind: 'portfolio', label: 'Across accounts',  summary: 'Total exposure, and which account fails first.', minWidth: 320, symbolScoped: false, streams: false, multiple: false },
  notes:     { kind: 'notes',     label: 'Notes',            summary: "The day's notebook entry.",                     minWidth: 260, symbolScoped: false, streams: false, multiple: false },
};

/**
 * The spec for a panel, tolerating anything.
 *
 * A workspace is stored JSON that outlives the build that wrote it. A panel
 * removed in a later version is a real state, and it must resolve to null so
 * the loader can DROP it rather than render a hole.
 */
export function panelSpec(kind: string | null | undefined): PanelSpec | null {
  // `hasOwn`, not `in`: a plain object inherits from Object.prototype, so
  // `'toString' in SPECS` is true and the lookup returns a function.
  return typeof kind === 'string' && Object.hasOwn(SPECS, kind)
    ? SPECS[kind as PanelKind]
    : null;
}

export function isPanelKind(kind: string | null | undefined): kind is PanelKind {
  return panelSpec(kind) !== null;
}

/** Every spec, in picker order. */
export function panelSpecs(): readonly PanelSpec[] {
  return PANEL_KINDS.map((k) => SPECS[k]);
}
